<template>
    <div
        v-if="show_confirm"
        class="p-6 text-center"
    >
        <base-svg
            name="illustration-envelope-tick"
            class="w-full mx-auto mb-6 max-w-2xs"
        />

        <h6 class="mb-3 text-lg font-normal font-semibold">
            You've got mail!
        </h6>

        <p class="text-grey-darker">
            Your account has been successfully registered. To activate it, please confirm your email address
            by clicking on the link we’ve just sent you.
        </p>
    </div>

    <users-store
        v-else-if="show_step_2"
        :sid="sid"
        v-slot="{
            error,
            is_loading: is_saving,
            has_loaded,
            store
        }"
        @success="onUsersStoreSuccess"
    >
        <recaptcha-form
            v-model="form.recaptcha"
            @submit="store(_.extend(form, {
                intended: $route.query.intended
            }))"
        >
            <div class="px-6 pt-6 border-b">
                <base-alert
                    v-if="$route.query.intention === 'listing-message'"
                    level="info"
                >
                    <template slot="heading">
                        Account required
                    </template>

                    <template slot="content">
                        We'll send your message once you've created an account.
                    </template>
                </base-alert>

                <validator-alert :sid="sid" />

                <form-item
                    :sid="sid"
                    :is-required="true"
                    name="persona"
                >
                    <template slot="label">Profile</template>

                    <input-select
                        v-model="form.persona"
                        :sid="sid"
                        :disabled="is_saving"
                        :options="[
                            { value: 'landlord', label: 'I\'m a Landlord / Agent' },
                            { value: 'tenant', label: 'I\'m a Tenant' }
                        ]"
                        name="term"
                    />
                </form-item>

                <form-item
                    :sid="sid"
                    :is-required="true"
                    name="email"
                >
                    <template slot="label">Email</template>

                    <input-text
                        v-model="form.email"
                        :sid="sid"
                        :disabled="is_saving"
                        name="email"
                        type="email"
                    />
                </form-item>

                <form-item
                    :sid="sid"
                    :is-required="true"
                    name="password"
                >
                    <template slot="label">Password</template>

                    <input-text
                        v-model="form.password"
                        :sid="sid"
                        :disabled="is_saving"
                        name="password"
                        type="password"
                    />
                </form-item>

                <button
                    v-if="!show_referral_code_input"
                    type="button"
                    class="flex items-center justify-center w-full mb-3 -mt-3 text-xs text-center text-blue focus:outline-none"
                    @click.prevent="show_referral_code_input = true"
                >
                    <div>Got a referral code?</div>

                    <base-svg
                        name="icon-cheveron-down"
                        class="w-4 h-4 ml-1 fill-primary-blue"
                    />
                </button>

                <form-item
                    v-else
                    :sid="sid"
                    name="referral_code"
                >
                    <template slot="label">Referral code</template>

                    <input-text
                        v-model="form.referral_code"
                        :sid="sid"
                        :disabled="is_saving"
                        name="referral_code"
                        type="text"
                    />
                </form-item>
            </div>

            <div class="px-6 py-3 text-right rounded-b bg-grey-bg">
                <base-button
                    :is-loading="is_saving"
                    class="w-full btn-blue"
                >
                    Create Account
                </base-button>

                <div class="pt-3 text-xs leading-tight text-center text-grey-darker">
                    By registering your account, you agree to our
                    <laroute-link
                        :to="{ name: 'www.policies.terms.index' }"
                        class="text-blue"
                    >
                        Terms,
                    </laroute-link>

                    <laroute-link
                        :to="{ name: 'www.policies.privacy.index' }"
                        class="text-blue"
                    >
                        Privacy
                    </laroute-link>

                    &amp;

                    <laroute-link
                        :to="{ name: 'www.policies.cookies.index' }"
                        class="text-blue"
                    >
                        Cookie
                    </laroute-link>

                    policies.

                    This site is protected by reCAPTCHA and the Google
                    <a
                        href="https://policies.google.com/privacy"
                        class="text-blue"
                    >
                        Privacy Policy
                    </a>
                    and
                    <a
                        href="https://policies.google.com/terms"
                        class="no-underline text-blue"
                    >
                        Terms of Service
                    </a>
                    apply.
                </div>
            </div>

            <error-modal
                v-if="error && $error.is(error, { exclude: [400, 422] })"
                :error="error"
            />
        </recaptcha-form>
    </users-store>

    <callback-requests-store
        v-else
        :sid="sid"
        v-slot="{
            is_loading: is_saving,
            error,
            store
        }"
        @success="onCallbackRequestsStoreSuccess"
    >
        <recaptcha-form
            v-model="form.recaptcha"
            @submit="store(form)"
        >
            <div class="px-6 pt-6 border-b">
                <base-alert
                    v-if="$route.query.intention === 'listing-message'"
                    level="info"
                >
                    <template slot="heading">
                        Account required
                    </template>

                    <template slot="content">
                        We'll send your message once you've created an account.
                    </template>
                </base-alert>

                <validator-alert :sid="sid" />

                <form-item
                    :sid="sid"
                    :is-required="true"
                    name="first_name"
                >
                    <template slot="label">First Name</template>

                    <input-text
                        v-model="form.first_name"
                        :sid="sid"
                        :disabled="is_saving"
                        name="first_name"
                    />
                </form-item>

                <form-item
                    :sid="sid"
                    :is-required="true"
                    name="last_name"
                >
                    <template slot="label">Last Name</template>

                    <input-text
                        v-model="form.last_name"
                        :sid="sid"
                        :disabled="is_saving"
                        name="last_name"
                    />
                </form-item>

                <form-item
                    :sid="sid"
                    :is-required="true"
                    name="telephone"
                >
                    <template slot="label">Telephone</template>

                    <input-telephone
                        v-model="form.telephone"
                        :sid="sid"
                        :disabled="is_saving"
                        placeholder="7000 000 000"
                        name="telephone"
                    />
                </form-item>
            </div>

            <div class="px-6 py-3 text-right rounded-b bg-grey-bg">
                <base-button
                    :is-loading="is_saving"
                    class="w-full btn-blue"
                >
                    Next
                </base-button>

                <div class="pt-3 text-xs leading-tight text-center text-grey-darker">
                    By clicking 'Next', you agree to our
                    <laroute-link
                        :to="{ name: 'www.policies.terms.index' }"
                        class="text-blue"
                    >
                        Terms,
                    </laroute-link>

                    <laroute-link
                        :to="{ name: 'www.policies.privacy.index' }"
                        class="text-blue"
                    >
                        Privacy
                    </laroute-link>

                    &amp;

                    <laroute-link
                        :to="{ name: 'www.policies.cookies.index' }"
                        class="text-blue"
                    >
                        Cookie
                    </laroute-link>

                    policies.

                    This site is protected by reCAPTCHA and the Google
                    <a
                        href="https://policies.google.com/privacy"
                        class="text-blue"
                    >
                        Privacy Policy
                    </a>
                    and
                    <a
                        href="https://policies.google.com/terms"
                        class="no-underline text-blue"
                    >
                        Terms of Service
                    </a>
                    apply.
                </div>
            </div>

            <error-modal
                v-if="error && $error.is(error, { exclude: [400, 422] })"
                :error="error"
            />
        </recaptcha-form>
    </callback-requests-store>
</template>

<script>
    import _ from 'lodash'
    import local from '../../../store/local'
    import HeadMixin from '../../../mixins/head'
    import CallbackRequestsStore from '../../../components/callback-requests-store'
    import UsersStore from '../../../components/users-store'

    export default {
        mixins: [
            HeadMixin
        ],
        components: {
            CallbackRequestsStore,
            UsersStore
        },
        data () {
            return {
                sid: _.uuid(),
                show_confirm: false,
                show_step_2: false,
                show_referral_code_input: !!this.$route.query.referral_code,
                form: {
                    recaptcha: '',
                    persona: 'landlord',
                    first_name: '',
                    last_name: '',
                    email: '',
                    telephone: null,
                    password: '',
                    referral_code: this.$route.query.referral_code
                }
            }
        },
        watch: {
            'form.email' () {
                local.set('guest-email', this.form.email)
            }
        },
        created () {
            const url = this.$route.name === 'residencies.users.create'
                ? this.$laroute.route('residencies.sessions.create', { token: this.$route.params.token })
                : this.$laroute.route('auth.sessions.create')

            this.$validator.messages({
                'email.unique': this.$t('validation.unique', {
                    attribute: 'email'
                }) + ` <a href="${url}" class="text-inherit">Login instead?</a>`
            }, this.sid)

            if (!this.form.email) {
                this.form.email = local.get('guest-email')
            }
        },
        methods: {
            head () {
                return {
                    title: 'Create an account'
                }
            },
            onCallbackRequestsStoreSuccess () {
                setTimeout(() => {
                    this.show_step_2 = true
                }, 0)
            },
            onUsersStoreSuccess ({ meta }) {
                this.$track(
                    'auth.users.create',
                    'form-submission',
                    'A guest has created an account'
                )

                switch (_.get(meta, 'persona.data.object')) {
                    case 'landlord':
                        this.$track(
                            'auth.landlords.create',
                            'form-submission',
                            'A user has created a landlord persona'
                        )
                        break
                    case 'tenant':
                        this.$track(
                            'auth.tenants.create',
                            'form-submission',
                            'A user has created a tenant persona'
                        )
                        break
                }

                setTimeout(() => {
                    if (meta.intended) {
                        window.location = meta.intended
                    } else {
                        this.show_confirm = true
                    }
                }, 0)
            }
        }
    }
</script>
